import { useEffect, useRef, useState } from "react";
import { useT } from "../lib/useT";

/**
 * A command or value the user has to take somewhere else, with one tap to copy.
 *
 * Every screen that cannot fix a problem itself ends up handing over a string —
 * a pairing command, an origin, a prompt for the agent. Each of them owns its
 * own "copied" flash, so the state lives here rather than in three screens.
 *
 * The clipboard is unavailable outside secure contexts. That is not worth an
 * error: the text stays selectable either way, so a failed copy simply does not
 * flash.
 */
export function CopyBlock({
  value,
  inline = false,
  truncate = false,
}: {
  value: string;
  /** Sits inside a definition list rather than standing on its own line. */
  inline?: boolean;
  /** For an id long enough to need clipping instead of wrapping. */
  truncate?: boolean;
}) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
  }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => setCopied(false), 1_800);
    } catch {
      setCopied(false);
    }
  }

  return (
    <button
      type="button"
      className={inline ? "detail-value" : "code-block"}
      onClick={() => void copy()}
      title={t("action.copyToClipboard")}
    >
      {inline ? (
        truncate ? (
          <span className="truncate-id">{value}</span>
        ) : (
          value
        )
      ) : (
        <code>{value}</code>
      )}
      <span className="code-copy">{copied ? t("action.copied") : t("action.copy")}</span>
    </button>
  );
}
